import React, { useEffect } from 'react'
import { Row, Col } from 'react-bootstrap'
import classnames from 'classnames'
import { SelectionField } from '../common'
import SchedulingDateFields from './SchedulingDateFields'
import { getConditionalSessionLabel } from './schedulingHelpers'

/**
 * Component to render scheduling condition for a single session
 * @param {Object} props
 * @param {number} props.sessionIndex - Session number (1-based)
 * @param {Object} props.values - Formik values
 * @param {Function} props.setFieldValue - Formik setFieldValue
 * @param {Function} props.setFieldTouched - Formik setFieldTouched
 * @param {Object} props.errors - Formik errors
 * @param {Object} props.touched - Formik touched
 * @param {boolean} props.isSowStatusDraft - Whether SOW is in draft status
 * @param {boolean} props.coachingPlanWIP - Whether coaching plan is work in progress
 * @param {boolean} props.viewOnlyField - Whether fields should be view-only
 * @param {Object} props.user - Current user object
 * @param {boolean} props.showModal - Whether modal is showing
 * @param {Object} props.labelProps - Props for label styling
 */
const SchedulingConditionRow = ({
  sessionIndex,
  values,
  setFieldValue,
  setFieldTouched,
  errors,
  touched,
  isSowStatusDraft,
  coachingPlanWIP,
  viewOnlyField,
  user,
  showModal,
  labelProps,
}) => {
  const index = sessionIndex - 1
  const conditionFieldName = `schedulingConditions[${index}].condition`
  const sessionLabel = getConditionalSessionLabel(sessionIndex, values?.sowType)

  // Get errors for this session
  const schedulingErrors = errors?.schedulingConditions
  let sessionError = null
  let sessionErrorString = null

  if (schedulingErrors) {
    if (typeof schedulingErrors === 'string') {
      // If error is a string, check if it mentions this session or all sessions
      if (
        schedulingErrors.includes(`Session ${sessionIndex}`) ||
        schedulingErrors.includes(`session ${sessionIndex}`) ||
        schedulingErrors.includes(sessionLabel) ||
        schedulingErrors.includes(`all ${values.totalSession || 0} session`) ||
        schedulingErrors.includes('configure scheduling conditions')
      ) {
        sessionErrorString = schedulingErrors
      }
    } else if (Array.isArray(schedulingErrors)) {
      sessionError = schedulingErrors[index]
      if (typeof sessionError === 'string') {
        sessionErrorString = sessionError
      }
    } else if (schedulingErrors[index]) {
      sessionError = schedulingErrors[index]
      if (typeof sessionError === 'string') {
        sessionErrorString = sessionError
      }
    }
  }

  // Check if there are errors for condition
  const hasConditionError =
    (sessionError &&
      typeof sessionError === 'object' &&
      sessionError.condition) ||
    (sessionErrorString &&
      (sessionErrorString.includes('condition') ||
        sessionErrorString.includes(`Session ${sessionIndex}`) ||
        sessionErrorString.includes(sessionLabel)))

  // Check if any session has errors (for general error messages)
  const hasAnySessionError =
    schedulingErrors &&
    (typeof schedulingErrors === 'string' ||
      (Array.isArray(schedulingErrors) &&
        schedulingErrors.some((err) => err)) ||
      (typeof schedulingErrors === 'object' &&
        Object.keys(schedulingErrors).length > 0))

  // Check if field is touched or form has errors
  const isTouched =
    touched?.schedulingConditions?.[index] ||
    (hasAnySessionError && hasConditionError)

  // Mark fields as touched when there are errors
  useEffect(() => {
    if (hasAnySessionError && hasConditionError) {
      if (setFieldTouched) {
        setFieldTouched(`schedulingConditions[${index}]`, true)
      }
    }
  }, [hasAnySessionError, hasConditionError, index, setFieldTouched])

  // Initialize schedulingConditions array if needed
  useEffect(() => {
    const schedulingConditions = values.schedulingConditions || []
    if (!schedulingConditions[index]) {
      const newConditions = [...schedulingConditions]
      newConditions[index] = {
        sessionIndex,
        condition: '',
      }
      setFieldValue('schedulingConditions', newConditions)
    }
  }, [index, sessionIndex, setFieldValue, values.schedulingConditions])

  // Clear secondaryDate when condition changes from 'between' to something else
  const currentCondition = values.schedulingConditions?.[index]?.condition
  const currentSecondaryDate =
    values.schedulingConditions?.[index]?.secondaryDate
  useEffect(() => {
    if (
      currentCondition &&
      currentCondition !== 'between' &&
      currentSecondaryDate
    ) {
      setFieldValue(`schedulingConditions[${index}].secondaryDate`, '')
    }
  }, [currentCondition, currentSecondaryDate, index, setFieldValue])

  return (
    <div className='mb-4'>
      <Row className='mb-2'>
        <Col lg={12}>
          <h5 className='font-16 font-semibold mb-3'>{sessionLabel}</h5>
        </Col>
      </Row>
      <SelectionField
        label='CONDITION'
        labelProps={labelProps}
        name={conditionFieldName}
        size='lg'
        options={[
          { label: 'On or Before', value: 'on_or_before' },
          { label: 'On or After', value: 'on_or_after' },
          { label: 'Between', value: 'between' },
        ]}
        getValueAsOption={true}
        optionplaceholder='Select Condition'
        disabled={
          isSowStatusDraft || coachingPlanWIP
            ? viewOnlyField && !user?.isAdmin
            : viewOnlyField || showModal
        }
        sm='10'
        isUseFormikSelectionField={true}
        className={classnames({
          'border border-danger': hasConditionError && isTouched,
        })}
      />
      {/* Date fields below condition dropdown */}
      <SchedulingDateFields
        index={index}
        sessionIndex={sessionIndex}
        values={values}
        errors={errors}
        touched={touched}
        isSowStatusDraft={isSowStatusDraft}
        coachingPlanWIP={coachingPlanWIP}
        viewOnlyField={viewOnlyField}
        user={user}
        showModal={showModal}
        labelProps={labelProps}
      />
    </div>
  )
}

export default SchedulingConditionRow
